import React from 'react';
import Popover from '@amzn/awsui-components-core/popover';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Tabs from '@amzn/awsui-components-core/tabs';
import Toggle from '@amzn/awsui-components-core/toggle';
import type { StarRocksQuery } from 'src/components/star-grid/api/reportEngineTypes';
import type { PnlQueryStats } from 'src/components/star-grid/queryStats.types';
import { ReportStatsApiCalls } from 'src/components/star-grid/components/ReportStatsApiCalls';
import { ReportStatsQueries } from 'src/components/star-grid/components/ReportStatsQueries';
import { ReportStatsSummary, type QueryStatsSource } from 'src/components/star-grid/components/ReportStatsSummary';
import { useStarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { isColdQueryMode, setColdQueryMode } from 'src/components/star-grid/services/coldQueryMode';
import styles from 'src/components/star-grid/views/styles/ReportDetailView.module.scss';

export interface ReportStatsPanelProps {
  readonly queries: readonly StarRocksQuery[];
  readonly serverMs?: number | null;
  readonly queryStats?: PnlQueryStats;
  readonly statsSource?: QueryStatsSource;
  readonly onExplain?: () => Promise<readonly string[]>;
}

/**
 * Show stats: what the last load cost, every StarRocks query it ran, and every API call the page made,
 * with a switch to re-run the page as a pure cold read.
 */
export const ReportStatsPanel: React.FC<ReportStatsPanelProps> = ({ queries, serverMs, queryStats, statsSource, onExplain }) => {
  const text = useStarGridText();
  const cold = isColdQueryMode();
  return (
    <Popover
      triggerType="text"
      size="large"
      position="bottom"
      renderWithPortal
      header={text('statsHeader')}
      content={
        <SpaceBetween size="s">
          <Toggle checked={cold} onChange={({ detail }) => setColdQueryMode(detail.checked)} description={text('statsColdHelp')}>
            {text('statsColdToggle')}
          </Toggle>
          <Tabs
            variant="container"
            tabs={[
              {
                id: 'summary',
                label: text('statsSummaryTab'),
                content: <ReportStatsSummary serverMs={serverMs} queryStats={queryStats} source={statsSource} />
              },
              {
                id: 'queries',
                label: `${text('statsQueriesTab')} (${queries.length})`,
                content: <ReportStatsQueries queries={queries} onExplain={onExplain} queryId={statsSource?.queryId} />
              },
              { id: 'api', label: text('statsApiTab'), content: <ReportStatsApiCalls /> }
            ]}
          />
        </SpaceBetween>
      }
    >
      <span className={`${styles.loadStats} ${styles.querySqlTrigger}`} data-testid="report-stats-trigger">
        {cold ? `${text('statsShow')} · ${text('statsCacheBypassed')}` : text('statsShow')}
      </span>
    </Popover>
  );
};
